import {
  Breadcrumbs,
  Button,
  Dialog,
  FormControl,
  IconButton,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { useHistory } from 'react-router';
import AddIcon from '@mui/icons-material/Add';
import { ColumnType, DataTable } from '../DataTable';
import { useEffect, useMemo, useState, ChangeEvent } from 'react';
import { accessType, ClassPlanProps, FundProps } from '../../redux-store/types/funds';
import { Location } from 'history';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { fundAccess, getPlanList, updatePlan } from '../../redux-store/actions/funds';
import { ConfirmationDialog } from '../commonComponents';
import ListPagination from '../common/ListPagination';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { currencyConversion } from '../../utils/utilityFunctions';
import {
  AccessToggle,
  RowActionsMenu,
  userMgmtBoxStyles,
  userMgmtRowStyles,
  userMgmtTableBodyStyles,
  userMgmtTableHeaderStyles,
} from '../userManagement/userMgmtListStyles';

const filterSelectSx = {
  minWidth: 72,
  height: 36,
  fontFamily: 'Inter, sans-serif',
  fontSize: 14,
  borderRadius: '8px',
  bgcolor: '#FFFFFF',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D1D5DB' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2057A6' },
};

export const AccessHeaderForPlan = (): JSX.Element => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box display="flex" flexDirection="column">
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 14,
            color: '#1F53BE',
            fontWeight: 500,
          }}
          component="span">
          Access
          <InfoOutlinedIcon
            color="info"
            fontSize="small"
            onClick={() => setOpen(true)}
            sx={{ position: 'relative', top: 4, ml: 0.5, cursor: 'pointer' }}
          />
        </Typography>
      </Box>
      <Dialog
        onClose={() => setOpen(false)}
        open={open}
        sx={{
          '.MuiPaper-root ': {
            maxWidth: 550,
            p: { xs: 2, sm: '15px 20px 45px' },
            borderRadius: '10px',
            height: 'unset',
            overflowY: 'auto',
          },
        }}>
        <IconButton onClick={() => setOpen(false)} sx={{ alignSelf: 'flex-end' }}>
          <CloseIcon fontSize="medium" />
        </IconButton>
        <Box sx={{ mb: 2, textAlign: 'center', '.MuiSvgIcon-root': { fontSize: '3.1875rem' } }}>
          <InfoOutlinedIcon color="info" fontSize="large" />
        </Box>
        <Box
          sx={{
            width: { xs: '100%', sm: '80%' },
            mx: 'auto',
            '.MuiTypography-root': {
              fontSize: 20,
              fontWeight: 500,
              textAlign: 'center',
            },
          }}>
          <Typography sx={{ color: 'secondary.main', lineHeight: '35px', mt: 2 }}>
            Please ensure that you have at least one active fund before disabling the Plan.
          </Typography>
        </Box>
      </Dialog>
    </>
  );
};

export default function ViewPlans({
  location,
}: {
  location: Location<{ fundId: string; PlanId: string }>;
}): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const { fundId } = location.state || {};
  const [isLoading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [plans, setPlans] = useState<ClassPlanProps[]>([]);
  const [schemeName, setSchemeName] = useState('N/A');
  const [access, setAccess] = useState(false);
  const [openDialog, setDialog] = useState<{ planData: ClassPlanProps } | null>(null);

  useEffect(() => {
    let isComponentActive = true;
    (async function () {
      try {
        setLoading(true);
        const { fundSetupAccess } = (await dispatch(fundAccess())) as unknown as accessType;
        if (fundId) {
          const res = (await dispatch(getPlanList(fundId))) as unknown as FundProps;
          if (!isComponentActive) return;
          setPlans(res?.plans || []);
          setSchemeName(res?.schemeName || 'N/A');
        }
        if (!isComponentActive) return;
        setAccess(fundSetupAccess);
        setLoading(false);
      } catch (e) {
        setLoading(false);
        console.log((e as Error).message);
      }
    })();
    return () => {
      isComponentActive = false;
    };
  }, [dispatch, fundId]);

  const noOfPages = Math.max(1, Math.ceil(plans.length / rowsPerPage) || 1);

  useEffect(() => {
    if (currentPage > noOfPages) setCurrentPage(1);
  }, [noOfPages, currentPage]);

  const pagedPlans = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return plans.slice(start, start + rowsPerPage);
  }, [plans, currentPage, rowsPerPage]);

  const rangeStart = plans.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const rangeEnd = Math.min(currentPage * rowsPerPage, plans.length);

  const columns: ColumnType[] = useMemo(
    () => [
      {
        header: 'Plan Code',
        align: 'left',
        headerAlign: 'left',
        renderCell: (row: ClassPlanProps) => (
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 14,
              fontWeight: 400,
              color: '#111927',
            }}>
            {row.planCode || 'N/A'}
          </Typography>
        ),
      },
      {
        header: 'Plan Description',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (row: ClassPlanProps) => row.planDescription || 'N/A',
      },
      {
        header: 'Minimum Commitment Amount',
        align: 'left',
        headerAlign: 'left',
        renderCell: (row: ClassPlanProps) => (
          <Box>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 14,
                fontWeight: 400,
                color: '#111927',
                lineHeight: 1.3,
              }}>
              {row.minCommitmentAmount != null
                ? Number(row.minCommitmentAmount).toLocaleString('en-IN')
                : 'N/A'}
            </Typography>
            {row.minCommitmentAmount != null && (
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 12,
                  fontWeight: 400,
                  color: '#6B7280',
                  lineHeight: 1.3,
                  mt: 0.25,
                }}>
                {currencyConversion(Number(row.minCommitmentAmount), row?.currency)}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        header: AccessHeaderForPlan,
        align: 'left',
        headerAlign: 'left',
        renderCell: (row: ClassPlanProps) => (
          <AccessToggle
            checked={!!row.isActive}
            onChange={() => setDialog({ planData: row })}
            disabled={!access}
          />
        ),
      },
      {
        header: 'Actions',
        align: 'left',
        headerAlign: 'left',
        renderCell: (row: ClassPlanProps) => (
          <RowActionsMenu
            items={[
              {
                label: 'View Plan Details',
                icon: '/images/view-application.svg',
                to: { pathname: 'plan-details', state: { PlanId: row.id, fundId } },
              },
              ...(access
                ? [
                    {
                      label: 'Edit Plan',
                      icon: '/images/edit.svg',
                      to: { pathname: 'edit-plan', state: { PlanId: row.id, fundId } },
                    },
                  ]
                : []),
            ]}
          />
        ),
      },
    ],
    [access, fundId]
  );

  return (
    <>
      <Box sx={{ bgcolor: 'transparent', px: { xs: 2, sm: 4 }, pt: { xs: 2, sm: 3 }, pb: 3 }}>
        <Breadcrumbs
          separator=">"
          aria-label="breadcrumb"
          sx={{
            mb: 1.5,
            '& .MuiBreadcrumbs-separator': { mx: 0.75, color: '#6C737F' },
            '.MuiTypography-root, .MuiLink-root': {
              fontSize: 14,
              color: '#111927',
              fontFamily: 'Inter, sans-serif',
            },
          }}>
          <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit">
            Home
          </Link>
          <Link
            component={RouterLink}
            to="/funds-setup/fund-list"
            underline="hover"
            color="inherit"
            sx={{ color: '#111927 !important' }}>
            Fund Set-Up
          </Link>
          <Typography sx={{ fontWeight: 500, color: '#6C737F !important' }}>Plan Details</Typography>
        </Breadcrumbs>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            mb: 2.5,
          }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: { xs: 20, sm: 24 },
              fontWeight: 700,
              color: '#092056',
              lineHeight: 1.2,
            }}>
            Plan Details
          </Typography>
          {access && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: 14,
                lineHeight: 1,
                textTransform: 'none',
                bgcolor: '#2057A6',
                color: '#FFFFFF',
                borderRadius: '8px',
                px: 2,
                py: 1.25,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#1A4A8C',
                  boxShadow: 'none',
                },
              }}
              onClick={() => history.push('add-plan', { fundId })}>
              Add Plan
            </Button>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,
            flexWrap: 'wrap',
            bgcolor: '#FFFFFF',
            borderRadius: '16px',
            border: '1px solid #F2F4F7',
            boxShadow: '0px 4px 16px rgba(26, 43, 60, 0.04)',
            px: { xs: 2.5, sm: 3 },
            py: 2.5,
            mb: 3,
          }}>
          <Box>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 12,
                fontWeight: 500,
                color: '#6B7280',
                mb: 0.75,
              }}>
              Scheme Name
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 16,
                fontWeight: 700,
                color: '#111927',
              }}>
              {schemeName}
            </Typography>
          </Box>
          <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 12,
                fontWeight: 500,
                color: '#6B7280',
                mb: 0.75,
              }}>
              Total Plans
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 16,
                fontWeight: 700,
                color: '#111927',
              }}>
              {plans.length}
            </Typography>
          </Box>
        </Box>

        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 18,
            fontWeight: 700,
            color: '#111927',
            mb: 2,
          }}>
          Plans
        </Typography>

        <Box
          sx={{
            bgcolor: '#FFFFFF',
            borderRadius: '16px',
            border: '1px solid #F2F4F7',
            boxShadow: '0px 4px 16px rgba(26, 43, 60, 0.04)',
            p: { xs: 2, sm: 2.5 },
          }}>
          <DataTable
            tableData={pagedPlans}
            tableHeader={columns}
            isLoading={isLoading}
            currentPage={undefined}
            noOfPages={1}
            renderAdditionalRow={false}
            tableBodyCustomStyles={userMgmtTableBodyStyles}
            rowCustomStyles={userMgmtRowStyles}
            tableHeaderCustomStyles={userMgmtTableHeaderStyles}
            boxStyles={userMgmtBoxStyles}
          />

          {!isLoading && plans.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                pt: 2.5,
                mt: 1,
                borderTop: '1px solid #F2F4F7',
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 14,
                    color: '#6B7280',
                    whiteSpace: 'nowrap',
                  }}>
                  Rows per page
                </Typography>
                <FormControl size="small">
                  <Select
                    value={String(rowsPerPage)}
                    onChange={(e: SelectChangeEvent) => {
                      setRowsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    sx={filterSelectSx}>
                    {[10, 25, 50].map((n) => (
                      <MenuItem key={n} value={String(n)}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 14,
                    color: '#6B7280',
                    whiteSpace: 'nowrap',
                  }}>
                  {rangeStart}-{rangeEnd} of {plans.length}
                </Typography>
              </Box>

              <ListPagination
                count={noOfPages}
                page={currentPage}
                onChange={(_e: ChangeEvent<unknown>, value: number) => setCurrentPage(value)}
              />
            </Box>
          )}
        </Box>
      </Box>

      <ConfirmationDialog
        message={`Are you sure you want to ${
          openDialog?.planData.isActive ? 'disable' : 'enable'
        } the access to this Plan ?`}
        open={openDialog !== null}
        setOpen={() => setDialog(null)}
        onSave={async () => {
          setDialog(null);
          try {
            if (!openDialog) return;
            const updatedRes = (await dispatch(
              updatePlan(Number(openDialog.planData.id), {
                ...openDialog.planData,
                isActive: !openDialog.planData.isActive,
              })
            )) as unknown as ClassPlanProps;
            setPlans((prev) =>
              prev.map((plan) => {
                if (Number(plan.id) === Number(updatedRes.id)) {
                  return { ...plan, ...updatedRes };
                }
                return plan;
              })
            );
          } catch (e) {
            console.error((e as Error).message);
          }
        }}
        onCancel={() => setDialog(null)}
      />
    </>
  );
}
